import { Method } from 'axios';
import { OrderLine } from './create-order';
import { Executable } from './executable';

/**
 * The GetTokenInput used by the constructor of CreateToken
 */
export interface GetTokenInput {
  /**
   * The orderId to retrieve a specific order.
   */
  tokenId?: number;
  /**
   * The bambu personId for identifying a person.
   */
  personId?: string;
  /**
   * The email address of the person.
   */
  email?: string;
  /**
   * Filters the tokens by the status code
   * @default 200
   * 0 - Pending
   * 200 - Created
   * 400 - Failed to mint
   */
  status_code?: number;

  /**
   * If not status code is provided, returns all tokens if provided
   */
  includeAllTokens?: boolean;
}

export interface Person {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  PersonId: string;
  metadata: any;
}

export interface Product {
  productId: number;
  name: string;
  description: string;
  payload: any;
  metadata: any;
  tokenId: number;
}

export interface Order {
  orderId: number;
  statusCode: number;
  externalOrderId: string;
  currency: string;
  orderLines?: OrderLine[];
}

export interface BambuToken {
  tokenId: number;
  name: string;
  externalTokenId: string;
  tokenTypeId: number;
  ledgerType: string;
  symbol: string;
  metadata: any;
  isNFT: boolean;
  supply: boolean;
  wipe: boolean;
  freeze: boolean;
  kyc: boolean;
  tenantId: number;
}

export interface Token {
  personId: string;
  tenantId: number;
  productId: number;
  orderId: number;
  orderLineId: number;
  tokenId: number;
  ledgerType: string;
  quantity: number;
  statusCode: number;
  person: Person;
  order: Order;
  orderLine: OrderLine;
  product: Product;
  token: BambuToken;
}

/**
 * The CreateToken response expected from the execution of the object within BambuClient execute.
 */
export interface GetTokenResponse {
  /**
   * the message response from the request
   */
  tokens: Token[];
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
}

/**
 * The create order api is an asyncronous request to BambuMeta to issue the provided user an order.  This request
 * will not guarentee an order is created.
 */
export class GetToken extends Executable<GetTokenResponse> {
  public tokenId?: number;

  public personId?: string;

  public email?: string;

  public status_code?: number;

  public includeAllTokens?: boolean = false;

  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'get';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the GetTokenResponse
   * @returns GetTokenResponse
   */
  public getResponse(apiResponse: any): GetTokenResponse {
    return {
      tokens: apiResponse,
    };
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    let url = `${tenantId}/tokens`;
    if (this.tokenId) {
      url += `?pending_token_id=${this.tokenId}`;
    } else if (this.personId) {
      url += `?person_id=${this.personId}`;
    } else if (this.email) {
      url += `?email=${encodeURIComponent(this.email)}`;
    }
    if (this.includeAllTokens) {
      url += `&includeAllTokens=Y`;
    }
    if (this.status_code) {
      url += `&status_code=${this.status_code}`;
    }
    return url;
  }

  constructor(input?: GetTokenInput) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    return undefined;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
